import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../utils/platform';
import { getServices, createService, getServiceQrToken } from '../../services/api';
import { FiPlus, FiX, FiCheckSquare, FiCamera, FiPrinter } from 'react-icons/fi';
import { QRCodeCanvas } from 'qrcode.react';
import Toast from '../../components/Toast';

const RecordAttendance = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'sunday', date: new Date().toISOString().slice(0,10), time: '', location: '' });
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [qrModal, setQrModal] = useState(null);

  const openQr = async (s) => {
    try {
      const r = await getServiceQrToken(s.id);
      setQrModal({ service: s, token: r.data.token });
    } catch { showToast(t('myProfile.qrLoadFailed'), 'error'); }
  };

  const printQr = () => {
    const w = window.open('', '_blank', 'width=600,height=800');
    const canvas = document.querySelector('#service-qr-canvas');
    if (!canvas || !qrModal) return;
    const dataUrl = canvas.toDataURL('image/png');
    w.document.write(`
      <html><head><title>${t('recordAttendance.attendanceQr')} — ${qrModal.service.name}</title>
      <style>body{font-family:sans-serif;text-align:center;padding:40px}h1{margin:0 0 8px}h2{color:#6b7280;font-weight:400;margin-top:0}img{margin:24px auto;display:block;max-width:90vw;height:auto}</style>
      </head><body>
        <h1>${qrModal.service.name}</h1>
        <h2>${qrModal.service.date}</h2>
        <img src="${dataUrl}" />
        <p style="font-size:18px"><strong>${t('recordAttendance.printScanLine')}</strong></p>
        <p style="color:#6b7280">${t('recordAttendance.printScanInstructions')}</p>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const fetch = () => getServices({ from: new Date(Date.now()-30*24*60*60*1000).toISOString().slice(0,10) }).then(r => setServices(Array.isArray(r.data) ? r.data : [])).catch(() => {});

  useEffect(() => { fetch(); }, []);

  const handleCreateService = async () => {
    setError('');
    if (!form.name || !form.date) return setError(t('recordAttendance.nameDateRequired'));
    setSaving(true);
    try { await createService(form); setShowForm(false); setForm({ name:'', type:'sunday', date: new Date().toISOString().slice(0,10), time:'', location:'' }); showToast(t('recordAttendance.serviceCreated')); fetch(); }
    catch (err) { setError(err.response?.data?.error || t('myProfile.failed')); }
    finally { setSaving(false); }
  };

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const typeLabel = { sunday: t('service.sunday'), midweek: t('service.midweek'), special: t('service.special'), cell: t('service.cell') };
  const typePill = { sunday: 'pill-purple', midweek: 'pill-blue', special: 'pill-green', cell: 'pill-yellow' };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.recordAttendance')}</h1></div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}><FiPlus /> {t('common.add')}</button>
      </div>

      <div className="table-card">
        <table>
          <thead><tr><th>{t('common.date')}</th><th>{t('recordAttendance.service')}</th><th>{t('recordAttendance.type')}</th><th>{t('recordAttendance.location')}</th><th>{t('recordAttendance.count')}</th><th>{t('common.actions')}</th></tr></thead>
          <tbody>
            {services.length === 0 ? (
              <tr><td colSpan={6}><div className="empty-state"><div className="empty-icon"><FiCheckSquare /></div><p>{t('recordAttendance.noServicesYet')}</p></div></td></tr>
            ) : services.map(s => (
              <tr key={s.id}>
                <td data-label={t('common.date')}>{s.date}</td>
                <td data-label={t('recordAttendance.service')}><strong>{s.name}</strong></td>
                <td data-label={t('recordAttendance.type')}><span className={`pill ${typePill[s.type]}`}>{typeLabel[s.type]}</span></td>
                <td data-label={t('recordAttendance.location')}>{s.location || '—'}</td>
                <td data-label={t('recordAttendance.count')}><strong>{s.actual_count}</strong></td>
                <td data-label={t('common.actions')}>
                  <div className="action-btns">
                    <button className="btn btn-sm btn-primary" onClick={() => openQr(s)}><FiCamera /> {t('recordAttendance.attendanceQr')}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New Service Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <div><h3>{t('recordAttendance.newServiceEvent')}</h3></div>
              <button className="btn-icon" onClick={() => setShowForm(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-grid">
                <div className="form-group form-full"><label>{t('recordAttendance.serviceName')} <span className="required">*</span></label><input value={form.name} onChange={set('name')} placeholder={t('recordAttendance.serviceNamePlaceholder')} /></div>
                <div className="form-group"><label>{t('recordAttendance.type')}</label><select value={form.type} onChange={set('type')}><option value="sunday">{t('service.sunday')}</option><option value="midweek">{t('service.midweek')}</option><option value="special">{t('service.special')}</option><option value="cell">{t('service.cell')}</option></select></div>
                <div className="form-group"><label>{t('common.date')} <span className="required">*</span></label><input type="date" value={form.date} onChange={set('date')} /></div>
                <div className="form-group"><label>{t('recordAttendance.time')}</label><input type="time" value={form.time} onChange={set('time')} /></div>
                <div className="form-group"><label>{t('recordAttendance.location')}</label><input value={form.location} onChange={set('location')} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleCreateService} disabled={saving}>{saving ? t('common.saving') : t('recordAttendance.createService')}</button>
            </div>
          </div>
        </div>
      )}

      {qrModal && (
        <div className="modal-overlay" onClick={() => setQrModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div><h3>{t('recordAttendance.attendanceQr')}</h3><p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>{qrModal.service.name} — {qrModal.service.date}</p></div>
              <button className="btn-icon" onClick={() => setQrModal(null)}><FiX /></button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: 24 }}>
              <div style={{ background: '#fff', padding: 16, border: '2px solid #e5e7eb', borderRadius: 12, display: 'inline-block' }}>
                <QRCodeCanvas id="service-qr-canvas" value={qrModal.token} size={240} />
              </div>
              <p style={{ color: '#6b7280', fontSize: 13, marginTop: 16 }}>{t('recordAttendance.qrPrintInstructions')}</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
                {!isMobile && <button className="btn btn-primary" onClick={printQr}><FiPrinter /> {t('recordAttendance.print')}</button>}
                <button className="btn btn-secondary" onClick={() => setQrModal(null)}>{t('common.close')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default RecordAttendance;
